import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { PromptConfirmation } from 'ui';
import { UserSettingsTranslation } from './constants';
import { endpoint } from './endpoints';
import { ComplexValue, SharedState } from 'app/types-shared';
import { ButtonTertiary, ButtonCallToAction } from 'ui';
import {
  FIELD_TYPE, SimpleFormValidationConfig, requiredValidation, validateEmail
} from 'app/infrastructure/simple-form';
import { SerializeType } from 'app/infrastructure/simple-form/types';
import { defaultSerializeMappings } from 'app/infrastructure/simple-form/serialize';
import { safeParam } from 'app/infrastructure/data/sanitize';
import { MODAL } from 'app/shell/modal-host-types';
import { isLocalLogin, isExternalUserHandlingEnabled } from 'app/infrastructure/hosting';
import { maxDescriptionLength } from 'app/area/user-profile/constants';
import { Tooltip } from 'ui/common/tooltip/tooltip';
import { SimpleFormComponentProps } from 'app/infrastructure/simple-form/types';
import { AppSetting } from 'app/infrastructure/hosting/app-settings';
import { RouterProps } from 'app/components/enhancers/with-router';
interface SettingsFormProps {
  shouldDisplayModal: boolean;
  translation: UserSettingsTranslation;
  router: RouterProps;
  sharedState: SharedState;
  locale?: string;
  formComponent: React.ComponentType<SimpleFormComponentProps>;
  continue: (value) => void;
  cancel: () => void;
  saveChanges: (query) => void;
  displayModal: (modalType: string) => void;
  validate: (config: SimpleFormValidationConfig, onSuccess: () => void, translation: any) => void;
  serialize: (mappings: any) => any;
  clearValidation: () => void;
}

export class SettingsForm extends React.Component<SettingsFormProps> {

  private hasFocus = (refName: string): boolean => {
    return safeParam(this.props.router.location.query.focus as string) &&
      safeParam(this.props.router.location.query.focus as string) === refName;
  };

  private setInitialValue = (value: ComplexValue<number>) => {
    const newValue = {
      ...value,
      description: value?.allLevelDescription
    };

    return !!value?.allLevelDescription ? newValue : value;
  };

  private validationConfig: SimpleFormValidationConfig = [
    requiredValidation('name'),
    requiredValidation('language'),
    requiredValidation('usergroup'),
    validateEmail('email')
  ];

  private saveChanges = () => {
    const { validate, serialize, saveChanges, translation } = this.props;

    validate(this.validationConfig, () => saveChanges(serialize(defaultSerializeMappings)), translation.validation_error);
  };

  private onChangePasswordModal = (e: any) => {
    e.preventDefault();
    this.props.displayModal(MODAL.changePassword);
  };

  private onSetupMultiFactorAutentication = () =>
    navigate.toSetupMultiFactorAutentication(AppSetting.OidcServiceUrl);

  private onDisableMultiFactorAutentication = () =>
    navigate.toDisableMultiFactorAutentication(AppSetting.OidcServiceUrl);

  public componentWillUnmount() {
    this.props.clearValidation();
  }

  public render() {
    const { translation, sharedState, formComponent: SimpleForm } = this.props;
    const readOnlyExternalUserHandling = isExternalUserHandlingEnabled();
    const isLocal = isLocalLogin(AppSetting.OidcServiceUrl, AppSetting.OidcClientId);

    return <section>
      <div className="main-grid">
        {this.props.shouldDisplayModal
          ? <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back_to_profile}
            onContinue={this.props.continue}
            onCancel={this.props.cancel}
          />
          : ''}
        <div className="row">
          <div className="col-lg-6">
            <SimpleForm
              name="name"
              maxLength={maxDescriptionLength}
              type={FIELD_TYPE.TEXTAREA}
              label={{ text: translation.name, label: 'user', required: true }}
              serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
              initialValue={sharedState.name}
              readOnly={!sharedState.myProfileAllowChangingName || readOnlyExternalUserHandling}
            />
          </div>
          <div className="col-lg-6">
            <SimpleForm
              name="email"
              maxLength={maxDescriptionLength}
              type={FIELD_TYPE.TEXTAREA}
              label={{ text: translation.email, label: 'email' }}
              serialize={{ type: SerializeType.PRIMITIVE_VALUE }}
              initialValue={sharedState.email}
              readOnly={!sharedState.myProfileAllowChangingEmailAddress || readOnlyExternalUserHandling}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6">
            <SimpleForm
              name="unit"
              type={FIELD_TYPE.SELECT_SINGLE_HIERARCHY}
              optionsEndpoint={endpoint.options.unit}
              label={{ text: translation.unit, label: 'unit' }}
              initialValue={this.setInitialValue(sharedState.unit as any)}
              translation={translation}
              placeholder={translation.search}
              hasFocus={this.hasFocus('unit')}
              serialize={{ type: SerializeType.V_VALUE }}
              descriptionAllLevels={true}
              infoText={translation.info_text_unit} />
          </div>
          <div className="col-lg-6">
            <SimpleForm
              name="location"
              type={FIELD_TYPE.SELECT_SINGLE_HIERARCHY}
              optionsEndpoint={endpoint.options.location}
              label={{ text: translation.location, label: 'location' }}
              initialValue={this.setInitialValue(sharedState.location as any)}
              translation={translation}
              placeholder={translation.search}
              hasFocus={this.hasFocus('location')}
              serialize={{ type: SerializeType.V_VALUE }}
              descriptionAllLevels={true}
              infoText={translation.info_text_location} />
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6">
            <SimpleForm
              name="usergroup"
              type={FIELD_TYPE.SELECT_SINGLE_HIERARCHY}
              optionsEndpoint={endpoint.options.usergroup}
              label={{ text: translation.usergroup, label: 'user-group', required: true }}
              initialValue={sharedState.userGroup}
              hasFocus={this.hasFocus('usergroup')}
              translation={translation}
              serialize={{ type: SerializeType.V_VALUE }}
              placeholder={translation.search} />

          </div>
          <div className="col-lg-6">
            <SimpleForm
              name="language"
              type={FIELD_TYPE.SELECT_SINGLE}
              optionsEndpoint={endpoint.options.language}
              label={{ text: translation.language, label: 'globe', required: true }}
              initialValue={sharedState.language}
              translation={translation}
              placeholder={translation.search}
              serialize={{ type: SerializeType.V_VALUE }}
              hasFocus={this.hasFocus('language')} />
          </div>
        </div>
        <div className="row">
          <div className="col-sm-10">
            <div className="text-start">
              {!readOnlyExternalUserHandling && isLocal
                && <ButtonTertiary onClick={this.onChangePasswordModal}>
                  {translation.change_password}
                </ButtonTertiary>}
              {isLocal
                && <Tooltip
                  title={translation.setup_mfa_description}
                  center={true} narrow={true}>
                  <ButtonTertiary onClick={this.onSetupMultiFactorAutentication}>
                    {translation.setup_mfa_title}
                  </ButtonTertiary>
                </Tooltip>}
              {isLocal
                && <Tooltip
                  title={translation.disable_mfa_description}
                  center={true} narrow={true}>
                  <ButtonTertiary onClick={this.onDisableMultiFactorAutentication}>
                    {translation.disable_mfa_title}
                  </ButtonTertiary>
                </Tooltip>}
            </div>
          </div>
          <div className="col-sm-2">
            <div className="text-end">
              <ButtonCallToAction onClick={this.saveChanges}>{translation.save}</ButtonCallToAction>
            </div>
          </div>
        </div>
      </div>
    </section>;
  }
}
